/**
 * dub-demo.js — one video, N voice tracks, switch language WITHOUT restarting.
 *
 * Why it exists: /video-dubbing is opened 3005 times per 90 days, a file is
 * picked 422 times and a run starts 49 times. People leave before uploading
 * anything, which means they never find out what the tool produces. The only
 * answer that costs the visitor nothing is to let them hear the result first.
 *
 * The point of the interaction is the SWITCH: you are 9 seconds into a clip, you
 * tap another language, and the same sentence continues in it — not from the
 * top. That is the whole product in one gesture, and it cannot be conveyed by a
 * screenshot or by five separate players.
 *
 * HOW IT STAYS IN SYNC. The video carries no audio at all; every language,
 * including the original, is a separate <audio> of the same length. Switching is
 * `next.currentTime = video.currentTime` then play — so position is preserved by
 * construction rather than by trying to keep five tracks running together.
 *
 * ⚠️ iOS. A media element may only start playing inside a user gesture, and that
 * permission is PER ELEMENT and for life (see audio-gate.js). Tracks are created
 * and armed up front — arming late means the second language a visitor picks is
 * refused with NotAllowedError, on the one platform where this tool is most
 * likely to be opened.
 *
 * Usage:  DubDemo.mount(rootEl, { base: '/data/dub-demo', strings: {...} })
 */
(function () {
    'use strict';
    if (window.DubDemo) return;

    // Native names: a language list is the one place translation makes things
    // worse — a French visitor looks for «Français», not «французский».
    var NATIVE = {
        ru: 'Русский', en: 'English', es: 'Español', fr: 'Français',
        ko: '한국어', pt: 'Português', de: 'Deutsch', it: 'Italiano',
        ar: 'العربية', id: 'Bahasa', uk: 'Українська', ja: '日本語', zh: '中文',
    };

    function el(tag, cls, text) {
        var e = document.createElement(tag);
        if (cls) e.className = cls;
        if (text != null) e.textContent = text;
        return e;
    }

    function mount(root, opts) {
        opts = opts || {};
        if (!root || root.__dubDemoMounted) return;
        root.__dubDemoMounted = true;

        var base = (opts.base || '/data/dub-demo').replace(/\/$/, '');
        var S = opts.strings || {};
        var tel = opts.tel || function () {};

        root.textContent = '';
        var status = el('div', 'dd-status', S.loading || '…');
        root.appendChild(status);

        // Returns a promise that settles when the DOM EXISTS. Without it a caller
        // that wants to touch the mounted markup — the page appends the money
        // price to the paid group's label once the per-buyer rate arrives — has
        // no way to know when that markup appeared, and quietly patches nothing.
        // Fire-and-forget is a contract that cannot be used correctly.
        return fetch(base + '/manifest.json', { credentials: 'same-origin' })
            .then(function (r) { if (!r.ok) throw new Error('http_' + r.status); return r.json(); })
            .then(function (man) { build(root, base, man, S, tel, status); })
            .catch(function (e) {
                status.textContent = S.failed || '';
                status.hidden = !S.failed;
                tel('demo_failed', { error: String((e && e.message) || e).slice(0, 60) });
            });
    }

    function build(root, base, man, S, tel, status) {
        // `key` distinguishes two tracks of the SAME language (ours / paid). An
        // older manifest has only `lang`; fall back to it so a stale file on a
        // CDN edge still renders instead of showing nothing.
        var tracks = (man.tracks || []).filter(function (t) { return t && t.lang && t.file; })
            .map(function (t) { return Object.assign({ key: t.key || t.lang, paid: !!t.paid }, t, { key: t.key || t.lang }); });
        if (!tracks.length) { status.textContent = S.failed || ''; return; }
        status.remove();

        var wrap = el('div', 'dd-wrap');

        var video = document.createElement('video');
        video.className = 'dd-video';
        video.src = base + '/' + (man.video || 'source.mp4');
        video.muted = true;                 // every voice is a separate track
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.preload = 'metadata';
        video.controls = true;
        wrap.appendChild(video);

        // One <audio> per language, all created and armed NOW: arming is per
        // element and only works inside a gesture (audio-gate.js).
        var audio = {};
        tracks.forEach(function (t) {
            var a = document.createElement('audio');
            a.src = base + '/' + t.file;
            a.preload = 'none';
            a.setAttribute('playsinline', '');
            audio[t.key] = a;
            wrap.appendChild(a);
            try { window.__audioGate && window.__audioGate.arm && window.__audioGate.arm(a); } catch (_) {}
        });

        // Две группы, а не один ряд. Наши голоса и платные различаются НЕ языком —
        // платный образец стоит на ТОМ ЖЕ языке, чтобы человек, стоя на девятой
        // секунде, услышал ту же фразу другим голосом. Один общий ряд превратил
        // бы это в «ещё пара языков» и разницу спрятал.
        var cur = tracks[0].key;
        var buttons = {};
        [false, true].forEach(function (paid) {
            var group = tracks.filter(function (t) { return !!t.paid === paid; });
            if (!group.length) return;
            var row = el('div', 'dd-group');
            var lbl = paid ? (S.group_paid || '') : (S.group_ours || '');
            if (lbl) {
                var le = el('div', 'dd-group-label', lbl);
                // Marked so the page can rewrite it once the money rate lands.
                // The rate is fetched per buyer and may not be here yet — and we
                // must NOT wait for it before mounting: arming the <audio>
                // elements only works inside the click that opened this block.
                if (paid) le.setAttribute('data-dd-paid-label', '');
                row.appendChild(le);
            }
            var chips = el('div', 'dd-chips');
            group.forEach(function (t) {
                var b = el('button', 'dd-chip' + (t.key === cur ? ' active' : ''), NATIVE[t.lang] || t.lang.toUpperCase());
                b.type = 'button';
                b.setAttribute('aria-pressed', t.key === cur ? 'true' : 'false');
                if (t.key === cur && S.original) b.title = S.original;
                b.addEventListener('click', function () { switchTo(t.key, true); });
                buttons[t.key] = b;
                chips.appendChild(b);
            });
            row.appendChild(chips);
            wrap.appendChild(row);
        });

        var caption = el('div', 'dd-caption', '');
        wrap.appendChild(caption);

        var note = el('div', 'dd-note', S.made_with || '');
        if (!S.made_with) note.hidden = true;
        wrap.appendChild(note);

        root.appendChild(wrap);

        function linesFor(key) {
            for (var i = 0; i < tracks.length; i++) if (tracks[i].key === key) return tracks[i].lines || [];
            return [];
        }

        function paintCaption() {
            var lines = linesFor(cur), t = video.currentTime || 0, txt = '';
            for (var i = 0; i < lines.length; i++) if (t + 0.15 >= lines[i].t) txt = lines[i].text;
            if (caption.textContent !== txt) caption.textContent = txt;
        }

        function switchTo(key, byUser) {
            if (!audio[key]) return;
            var lang = key;
            var from = cur;
            var at = video.currentTime || 0;
            var wasPlaying = !video.paused && !video.ended;

            Object.keys(audio).forEach(function (k) {
                if (k !== lang) { try { audio[k].pause(); } catch (_) {} }
            });
            cur = lang;
            Object.keys(buttons).forEach(function (k) {
                buttons[k].classList.toggle('active', k === lang);
                buttons[k].setAttribute('aria-pressed', k === lang ? 'true' : 'false');
            });

            var a = audio[lang];
            try { a.currentTime = at; } catch (_) {}
            if (wasPlaying) {
                // Not via __audioGate.play(): the element is already armed and the
                // bytes may not have arrived, so a plain play() here is the thing
                // arming exists to make legal.
                var p = a.play();
                if (p && p.catch) p.catch(function () {});
            }
            paintCaption();
            if (byUser) tel('demo_lang', { from: from, to: key, at: Math.round(at) });
        }

        video.addEventListener('play', function () {
            var a = audio[cur]; if (!a) return;
            try { a.currentTime = video.currentTime || 0; } catch (_) {}
            var p = a.play(); if (p && p.catch) p.catch(function () {});
            tel('demo_play', { lang: cur });
        });
        video.addEventListener('pause', function () { try { audio[cur].pause(); } catch (_) {} });
        video.addEventListener('ended', function () { try { audio[cur].pause(); } catch (_) {} });
        video.addEventListener('seeking', function () {
            try { audio[cur].currentTime = video.currentTime || 0; } catch (_) {}
        });
        /* Расходятся они не случайно: `video.currentTime` — НЕ часы реального
           времени. Под нехваткой процессора медиа-время элемента отстаёт от
           стены (замер 17.09.2026, Chrome на 2 ядрах, 1080×1920: 0.19…0.98× в
           разных конфигурациях). Поэтому догонять надо КАРТИНКУ, а не голос:
           перемотка играющего <audio> — это пере-буферизация и слышимый
           разрыв, а перемотка видео молча пропускает кадры. Тот же вывод, что
           у #7010 в /add-music-to-video и у предпросмотра /video-audio. */
        var lastPicFix = 0;
        video.addEventListener('timeupdate', function () {
            paintCaption();
            var a = audio[cur]; if (!a || a.paused) return;
            var drift = (a.currentTime || 0) - (video.currentTime || 0);
            if (Math.abs(drift) <= 0.35) return;
            var now = Date.now();
            if (now - lastPicFix < 1000) return;      // не чаще раза в секунду:
            lastPicFix = now;                          // каждая перемотка сбрасывает декодер
            try { video.currentTime = a.currentTime || 0; } catch (_) {}
        });

        paintCaption();
    }

    window.DubDemo = { mount: mount };
})();
